//=============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format timestamp to readable string
 */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString();
}

/**
 * Get relative time (e.g., "2 minutes ago")
 */
export function getRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy:', error);
    return false;
  }
}

/**
 * Validate participant name
 */
export function validateParticipantName(name: string): boolean {
  return name.trim().length > 0 && name.length <= 50;
}

/**
 * Validate meeting title
 */
export function validateMeetingTitle(title: string): boolean {
  return title.trim().length > 0 && title.length <= 100;
}

/**
 * Validate Meeting ID
 */
export function validateMeetingId(id: string): boolean {
  return id.trim().length > 0 && id.length <= 100;
}


/**
 * Generate meeting URL
 */
export function getMeetingUrl(meetingId: string): string {
  return `${window.location.origin}/meeting/${meetingId}`;
}

/**
 * Parse meeting ID from URL
 */
export function parseMeetingIdFromUrl(): string | null {
  const path = window.location.pathname;
  const match = path.match(/\/meeting\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

/**
 * Format duration (e.g., "1h 23m")
 */
export function formatDuration(startTime: string, endTime?: string): string {
  const start = new Date(startTime).getTime();
  const end = endTime ? new Date(endTime).getTime() : Date.now();
  const durationMs = end - start;

  const minutes = Math.floor(durationMs / (60 * 1000));
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }

  return `${minutes}m`;
}

/**
 * Check if browser supports required features
 */
export function checkBrowserSupport(): {
  isSupported: boolean;
  missingFeatures: string[];
} {
  const missingFeatures: string[] = [];

  if (!navigator.mediaDevices) {
    missingFeatures.push('Media Devices API');
  }

  if (!navigator.mediaDevices?.getDisplayMedia) {
    missingFeatures.push('Screen Capture API');
  }

  if (!window.RTCPeerConnection) {
    missingFeatures.push('WebRTC');
  }

  return {
    isSupported: missingFeatures.length === 0,
    missingFeatures,
  };
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}